/**
 * GameEndScreen - ゲーム終了画面コンポーネント
 *
 * 設定駆動: マップ固有の判定をせず、config から渡された設定に基づいて表示
 * + スコア登録フォーム + 実績表示
 */

import { useState, useEffect } from 'react';
import type { GameEndScreenProps } from '../types/ui-interfaces';
import { useTranslations } from '../i18n';
import type { TranslationKeys } from '../i18n/types';
import { getUIConfigByMapName } from '../config/gameRegistry';
import type { MapI18nKey } from '../config/gameRegistry';
import type { Achievement } from '../config/achievements';
import { checkAndUnlockAchievements } from '../utils/achievementChecker';
import { recordGameResult } from '../utils/statisticsRecorder';
import { checkScoreRank, submitScore, submitReplayArchive, type RankCheckResponse } from '../utils/scoreApi';
import { savePendingScore, clearPendingScore } from '../utils/storage';
import { BASE_PATH } from '../config';

export function GameEndScreen({
  endReason,
  player,
  completedLinksCount,
  actualTurn,
  onNewGame,
  onClose,
  mapName,
  puertoRicoScore,
  seattleScore,
  apiMapType,
  unifiedFinalScore,
  scoreMetadata,
  replayData,
}: GameEndScreenProps) {
  const t = useTranslations();

  // 設定を取得
  const { endScreen: config, i18nMapKey } = getUIConfigByMapName(mapName || '');

  const isBankrupt = endReason === 'BANKRUPTCY';
  const isVictory = endReason === 'VICTORY';
  // Seattle 固有の敗北理由
  const isDemandOverload = (endReason as string) === 'DEFEAT_DEMAND_OVERLOAD';
  const isSettlementFailed = (endReason as string) === 'DEFEAT_SETTLEMENT_FAILED';

  // ======== 日時 ========
  const [gameEndTime] = useState(() => new Date());

  // ======== ランキング登録 ========
  const [playerName, setPlayerName] = useState('');
  const [rankInfo, setRankInfo] = useState<RankCheckResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ success: boolean; rank?: number; message?: string } | null>(null);

  // ======== 実績 ========
  const [unlockedAchievements, setUnlockedAchievements] = useState<Achievement[]>([]);

  useEffect(() => {
    // 実績チェック + 統計記録
    if (apiMapType && scoreMetadata) {
      const meta = { sharesIssued: player.sharesIssued, ...scoreMetadata };
      const unlocked = checkAndUnlockAchievements(apiMapType, meta);
      setUnlockedAchievements(unlocked);

      // 統計記録
      if (unifiedFinalScore !== undefined) {
        recordGameResult(apiMapType, isVictory, meta, unifiedFinalScore);
      }
    }

    // ML用リプレイアーカイブ送信（サイレント、全結果対象）
    if (apiMapType && replayData) {
      submitReplayArchive({
        mapType: apiMapType,
        finalScore: unifiedFinalScore,
        turnCount: actualTurn,
        metadata: scoreMetadata || {},
        replayData: {
          initialState: replayData.initialState,
          moves: replayData.turnSnapshots,
          moveLog: replayData.moveLog,
        },
      });
    }

    // ランキング登録可否チェック（勝利時のみ）
    if (isVictory && apiMapType && unifiedFinalScore !== undefined) {
      checkScoreRank(apiMapType, unifiedFinalScore)
        .then(setRankInfo)
        .catch(() => {
          // サーバー未起動時はスキップ
        });
    }
  }, [apiMapType, unifiedFinalScore, scoreMetadata, player.sharesIssued]);

  const handleSubmitScore = async () => {
    if (!apiMapType || unifiedFinalScore === undefined || !playerName.trim()) return;
    setIsSubmitting(true);
    const requestData = {
      playerName: playerName.trim(),
      mapType: apiMapType,
      finalScore: unifiedFinalScore,
      turnCount: actualTurn,
      metadata: scoreMetadata || {},
      replayData: replayData
        ? { initialState: replayData.initialState, moves: replayData.turnSnapshots, moveLog: replayData.moveLog }
        : { initialState: {}, moves: [] },
    };
    try {
      const result = await submitScore(requestData);
      if (result.success) {
        clearPendingScore();
        setSubmitResult({ success: true, rank: result.rank, message: result.verification?.message });
      } else {
        savePendingScore({ ...requestData, savedAt: new Date().toISOString() });
        setSubmitResult({ success: false, message: result.error });
      }
    } catch {
      savePendingScore({ ...requestData, savedAt: new Date().toISOString() });
      setSubmitResult({ success: false, message: 'Server connection failed' });
    }
    setIsSubmitting(false);
  };

  /**
   * スコアに基づく評価メッセージを取得
   */
  const getEvaluationMessage = (): string | null => {
    // 途中破産の場合は共通の破産メッセージを表示（Detroit以外）
    // Detroit は破産がゲーム終了条件なので通常の評価を表示
    if (isBankrupt && config.scoreType !== 'turns') {
      return t.gameEnd.bankruptcyMessage;
    }

    if (!config.scoreEvaluations || config.scoreEvaluations.length === 0) return null;

    // スコアを計算
    let score: number;
    switch (config.scoreType) {
      case 'turns':
        score = actualTurn;
        break;
      case 'cash': {
        const buybackCost = player.sharesIssued * (config.shareBuybackCost || 5);
        score = player.finalCash ?? (player.cash - buybackCost);
        break;
      }
      case 'income':
        score = puertoRicoScore?.finalScore ?? 0;
        break;
      case 'finalScore':
        score = seattleScore?.finalScore ?? 0;
        break;
      default:
        return null;
    }

    // 評価を検索（降順でマッチング）
    const evaluation = config.scoreEvaluations.find(e => score >= e.minScore);
    if (!evaluation) return null;

    // i18n キーから評価メッセージを取得
    const mapEvaluations = t.gameEnd?.[i18nMapKey as keyof Pick<TranslationKeys['gameEnd'], MapI18nKey>];
    if (mapEvaluations && 'evaluations' in mapEvaluations) {
      const evalMessages = mapEvaluations.evaluations as Record<string, string>;
      return evalMessages[evaluation.key] || null;
    }
    return null;
  };

  // スコア計算
  const renderScoreDetails = () => {
    if (isBankrupt && config.scoreType !== 'turns') return null;

    switch (config.scoreType) {
      case 'cash': {
        // Barbados: 現金 - 株式買い戻しコスト
        const buybackCost = player.sharesIssued * (config.shareBuybackCost || 5);
        const finalCash = player.finalCash ?? (player.cash - buybackCost);
        return (
          <div className="flex items-center gap-2 text-xs md:text-sm bg-muted/50 rounded px-2 py-1">
            <span>${player.cash}</span>
            <span className="text-muted-foreground">-</span>
            <span className="text-destructive">${buybackCost}</span>
            <span className="text-muted-foreground">=</span>
            <span className={`font-bold ${finalCash >= 0 ? 'text-success' : 'text-destructive'}`}>
              ${finalCash}
            </span>
          </div>
        );
      }

      case 'income': {
        // Puerto Rico: 収入レベル - 黒キューブペナルティ
        if (!puertoRicoScore) return null;
        return (
          <div className="flex flex-col gap-1 text-xs md:text-sm bg-muted/50 rounded px-2 py-1">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Income:</span>
              <span>{puertoRicoScore.originalIncomeLevel}</span>
              <span className="text-muted-foreground">-</span>
              <span className="text-destructive">
                {puertoRicoScore.remainingBlackCubes}×{config.blackCubePenalty || 10}
              </span>
              <span className="text-muted-foreground">=</span>
              <span className={`font-bold ${puertoRicoScore.penalizedIncomeLevel >= 0 ? 'text-success' : 'text-destructive'}`}>
                {puertoRicoScore.penalizedIncomeLevel}
              </span>
            </div>
            {isVictory && puertoRicoScore.finalScore !== null && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Score:</span>
                <span>{puertoRicoScore.penalizedIncomeLevel}×{config.incomeMultiplier || 3}</span>
                <span className="text-muted-foreground">+</span>
                <span>{completedLinksCount}</span>
                <span className="text-muted-foreground">-</span>
                <span className="text-destructive">{player.sharesIssued}×{config.sharesPenalty || 3}</span>
                <span className="text-muted-foreground">=</span>
                <span className="font-bold text-success">{puertoRicoScore.finalScore}</span>
              </div>
            )}
          </div>
        );
      }

      case 'turns': {
        // Detroit: ターン数がスコア
        const threshold = config.victoryThreshold || 7;
        return (
          <div className="flex items-center gap-2 text-xs md:text-sm bg-muted/50 rounded px-2 py-1">
            <span className="text-muted-foreground">Score:</span>
            <span className={`font-bold ${isVictory ? 'text-success' : 'text-destructive'}`}>
              Turn {actualTurn}
            </span>
            <span className="text-muted-foreground">
              ({isVictory ? `${threshold}+ = Win` : `< ${threshold} = Lose`})
            </span>
          </div>
        );
      }

      case 'finalScore': {
        // Seattle Metro: 清算コスト + 得点計算
        if (!seattleScore) return null;

        return (
          <div className="flex flex-col gap-2 text-xs md:text-sm bg-muted/50 rounded px-3 py-2 max-w-md">
            {/* 需要過多で敗北の場合 */}
            {isDemandOverload && (
              <div className="text-destructive font-medium">
                {t.gameEnd?.seattle?.demandOverload || 'Transit demand exceeded capacity!'}
              </div>
            )}

            {/* 清算表示（需要過多でない場合） */}
            {!isDemandOverload && (
              <div className="space-y-1">
                <div className="text-muted-foreground font-medium border-b border-border pb-1">
                  {t.gameEnd?.seattle?.settlement || 'Settlement'}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span>{t.gameEnd?.seattle?.shares || 'Shares'}:</span>
                  <span>{seattleScore.sharesIssued}×$5</span>
                  <span className="text-muted-foreground">=</span>
                  <span className="text-destructive">${seattleScore.shareBuybackCost}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span>{t.gameEnd?.seattle?.actions || 'Actions'}:</span>
                  <span className="text-muted-foreground">
                    (Eng {seattleScore.engineerUsageCount} + Loco {seattleScore.locomotiveUsageCount})
                  </span>
                  <span>×$5</span>
                  <span className="text-muted-foreground">=</span>
                  <span className="text-destructive">${seattleScore.actionUsageCost}</span>
                </div>
                <div className="flex items-center gap-2 pt-1 border-t border-border/50">
                  <span>{t.gameEnd?.seattle?.total || 'Total'}:</span>
                  <span className={`font-bold ${seattleScore.settlementSuccess ? '' : 'text-destructive'}`}>
                    ${seattleScore.settlementTotalCost}
                  </span>
                  <span className="text-muted-foreground">←</span>
                  <span>{t.gameEnd?.seattle?.cash || 'Cash'}:</span>
                  <span>${seattleScore.cash}</span>
                  {seattleScore.settlementSuccess ? (
                    <span className="text-success">✓</span>
                  ) : (
                    <span className="text-destructive">✗</span>
                  )}
                </div>
                {isSettlementFailed && (
                  <div className="text-destructive font-medium pt-1">
                    {t.gameEnd?.seattle?.settlementFailed || 'Settlement Failed!'}
                  </div>
                )}
              </div>
            )}

            {/* 得点表示（清算成功時のみ） */}
            {!isDemandOverload && seattleScore.settlementSuccess && seattleScore.finalScore !== null && (
              <div className="space-y-2 pt-2 border-t border-border">
                <div className="text-muted-foreground font-medium">
                  {t.gameEnd?.seattle?.score || 'Score Calculation'}
                </div>
                {/* 計算式 */}
                <div className="text-[10px] md:text-xs text-muted-foreground bg-muted/30 px-2 py-1 rounded">
                  {t.gameEnd?.seattle?.scoreFormula || 'Score = Delivered − Remaining − Unconnected'}
                </div>
                {/* 詳細計算 */}
                <div className="space-y-1 text-xs md:text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t.gameEnd?.seattle?.delivered || 'Delivered cubes'}:</span>
                    <span className="text-success font-medium">+{seattleScore.deliveredCubes}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t.gameEnd?.seattle?.remaining || 'Remaining cubes'}:</span>
                    <span className="text-destructive font-medium">−{seattleScore.remainingGoods}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t.gameEnd?.seattle?.unconnected || 'Unconnected'}:</span>
                    <span className="text-destructive font-medium">−{seattleScore.unconnectedStations}</span>
                  </div>
                </div>
                {/* 最終スコア */}
                <div className="flex justify-between items-center pt-2 border-t border-border/50">
                  <span className="font-medium">{t.gameEnd?.seattle?.finalScore || 'Final Score'}:</span>
                  <span className={`font-bold text-lg ${seattleScore.finalScore >= 0 ? 'text-success' : 'text-destructive'}`}>
                    {seattleScore.finalScore >= 0 ? '+' : ''}{seattleScore.finalScore}
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/60 backdrop-blur-sm p-2 md:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Game Over"
    >
      <div className="bg-card rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-scale-in">
        {/* ヘッダー: 結果表示 */}
        <div className="text-center py-2 md:py-4 border-b border-border">
          <div className="font-heading font-bold text-2xl md:text-4xl">
            {isBankrupt && (
              <span data-result="bankruptcy" className="text-destructive">{t.gameEnd.bankruptcy}</span>
            )}
            {isVictory && (
              <span data-result="victory" className="text-success">{t.gameEnd.victory}</span>
            )}
            {isDemandOverload && (
              <span data-result="demand-overload" className="text-destructive">
                {t.gameEnd?.seattle?.demandOverloadTitle || 'Demand Overload'}
              </span>
            )}
            {isSettlementFailed && (
              <span data-result="settlement-failed" className="text-destructive">
                {t.gameEnd?.seattle?.settlementFailedTitle || 'Settlement Failed'}
              </span>
            )}
            {!isBankrupt && !isVictory && !isDemandOverload && !isSettlementFailed && (
              <span data-result="defeat" className="text-destructive">{t.gameEnd.defeat}</span>
            )}
          </div>
          {/* 日時 */}
          <div className="text-[10px] md:text-xs text-muted-foreground mt-1">
            {gameEndTime.toLocaleString()}
          </div>
          {/* 評価メッセージ */}
          {getEvaluationMessage() && (
            <div className="text-sm md:text-base text-foreground/80 italic mt-1 px-4">
              {getEvaluationMessage()}
            </div>
          )}
        </div>

        {/* ボディ: スコア詳細と統計 */}
        <div className="p-2 md:p-4 space-y-2 md:space-y-4">
          {/* スコア計算詳細 */}
          {renderScoreDetails()}

          {/* 統計（Seattle以外で表示） */}
          {config.scoreType !== 'finalScore' && (
            <div className="bg-muted/30 rounded-lg p-2 md:p-3">
              <div className="text-[10px] md:text-xs text-muted-foreground mb-1 md:mb-2 font-medium">
                {t.gameEnd.statsTitle}
              </div>
              <div className="grid grid-cols-2 gap-1 md:gap-2 text-xs md:text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t.gameEnd.turn}:</span>
                  <span className="font-medium">{actualTurn}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t.gameEnd.income}:</span>
                  <span className="font-medium">{player.incomeLevel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t.gameEnd.links}:</span>
                  <span className="font-medium">{completedLinksCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t.gameEnd.loco}:</span>
                  <span className="font-medium">{player.locomotiveLevel}</span>
                </div>
                {config.scoreType === 'income' && puertoRicoScore && (
                  <div className="flex justify-between col-span-2">
                    <span className="text-muted-foreground">
                      {t.goodsDisplay.colors.black}:
                    </span>
                    <span className="font-medium text-destructive">
                      {puertoRicoScore.remainingBlackCubes}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ランキング順位情報 */}
          {rankInfo && (
            <div className="bg-muted/30 rounded-lg p-2 md:p-3 text-xs md:text-sm">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Rank:</span>
                <span className="font-bold">
                  {rankInfo.estimatedRank} / {rankInfo.total || '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Monthly:</span>
                <span className="font-medium">
                  {rankInfo.monthlyRank} / {rankInfo.monthlyTotal || '—'}
                </span>
              </div>
            </div>
          )}

          {/* 獲得した実績 */}
          {unlockedAchievements.length > 0 && (
            <div className="bg-muted/30 rounded-lg p-2 md:p-3">
              <div className="text-[10px] md:text-xs text-muted-foreground mb-1 font-medium">
                Achievements Unlocked
              </div>
              {unlockedAchievements.map(a => (
                <div key={a.id} className="flex items-center gap-2 text-xs md:text-sm py-1">
                  <span>{a.icon}</span>
                  <span className="font-medium">{a.name}</span>
                  <span className="text-[10px] bg-success/20 text-success px-1 rounded">NEW</span>
                  <span className="text-muted-foreground text-[10px]">— {a.description}</span>
                </div>
              ))}
            </div>
          )}

          {/* スコア登録フォーム */}
          {apiMapType && rankInfo?.canRegister && !submitResult && (
            <div className="border-t border-border pt-2 md:pt-3 space-y-2">
              <div className="text-xs md:text-sm font-medium">
                Hall of Honor
              </div>
              <input
                type="text"
                placeholder="Player Name (max 50)"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value.slice(0, 50))}
                maxLength={50}
                className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background text-foreground"
              />
              <button
                className="btn-primary w-full py-2 text-sm font-medium"
                onClick={handleSubmitScore}
                disabled={!playerName.trim() || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Register Score'}
              </button>
            </div>
          )}

          {/* 登録結果 */}
          {submitResult && (
            <div className={`rounded-lg p-2 md:p-3 text-xs md:text-sm ${submitResult.success ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}`}>
              {submitResult.success ? (
                <div className="flex items-center justify-between">
                  <span>Registered! Rank: {submitResult.rank}</span>
                  <button
                    className="underline text-xs ml-2"
                    onClick={() => {
                      window.location.href = `${BASE_PATH}hall-of-honor?map=${apiMapType}&rank=${submitResult.rank}`;
                    }}
                  >
                    View Rankings
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span>{submitResult.message || 'Registration failed'}</span>
                  <button
                    className="underline text-xs ml-2"
                    onClick={() => setSubmitResult(null)}
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* フッター: ボタン */}
        <div className="p-2 md:p-4 pt-0 flex gap-2">
          {onClose && (
            <button
              className="btn-secondary flex-1 py-2 md:py-3 text-sm md:text-base font-medium"
              onClick={onClose}
              data-action="view-map"
            >
              {t.gameEnd.viewMap || 'View Map'}
            </button>
          )}
          <button
            className={`btn-primary py-2 md:py-3 text-sm md:text-base font-medium ${onClose ? 'flex-1' : 'w-full'}`}
            onClick={onNewGame}
            data-action="new-game"
          >
            {t.gameEnd.playAgain}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GameEndScreen;
